import { create } from "zustand";
import { capitalizeFirstLetter } from "../utils/capitalizeLetter";
import { validateCanadaPostalCode } from "../utils/validatePostalCode";

interface IncomeEntry {
	key: string;
	id: string;
}

interface IncomeState {
	addedIncomes: IncomeEntry[];
	incomeData: { [id: string]: any };
	setAddedIncomes: (incomes: IncomeEntry[]) => void;
	addIncome: (key: string, id: string) => void;
	removeIncome: (id: string) => void;
	updateIncomeData: (id: string, field: string, value: any) => void;
	validateIncomeFields: () => boolean;
}

const useIncomeStore = create<IncomeState>()(
	(set, get): IncomeState => ({
		addedIncomes: [],
		incomeData: {},

		setAddedIncomes: (incomes) => set({ addedIncomes: incomes }),

		addIncome: (key, id) =>
			set((state) => ({
				addedIncomes: [...state.addedIncomes, { key, id }],
				incomeData: {
					...state.incomeData,
					[id]: {},
				},
			})),

		removeIncome: (id) =>
			set((state) => {
				const updatedIncomeData = { ...state.incomeData };
				delete updatedIncomeData[id];
				return {
					addedIncomes: state.addedIncomes.filter((income) => income.id !== id),
					incomeData: updatedIncomeData,
				};
			}),

		updateIncomeData: (id, field, value) => {
			let formattedValue = value;
			if (["employerName", "jobTitle", "businessName"].includes(field)) {
				formattedValue = capitalizeFirstLetter(value);
			} else if (field === "postalCode") {
				formattedValue = value.toUpperCase();
			}

			set((state) => ({
				incomeData: {
					...state.incomeData,
					[id]: {
						...state.incomeData[id],
						[field]: formattedValue,
						errors: {
							...state.incomeData[id]?.errors,
							[field]:
								field === "postalCode" && !validateCanadaPostalCode(value)
									? "Invalid postal code format"
									: formattedValue
										? ""
										: "This field is required",
						},
					},
				},
			}));
		},

		validateIncomeFields: () => {
			let isValid = true;
			const updatedIncomeData = { ...get().incomeData };

			for (const { key, id } of get().addedIncomes) {
				const fieldErrors: { [field: string]: string } = {};

				const fieldsToValidate =
					key === "fullTime" || key === "partTime"
						? [
								"employerName",
								"jobTitle",
								"annualBasePay",
								"annualBonuses",
								"annualOvertime",
								"annualCommission",
								"startDate",
							]
						: key === "selfEmployed"
							? [
									"businessName",
									"dateEstablished",
									"approximateStartDate",
									"annualIncome",
									"operatingType",
									"jobTitle",
									"annualBasePay",
								]
							: key === "rentalIncome"
								? [
										"address",
										"city",
										"postalCode",
										"province",
										"propertyType",
										"propertyValue",
										"monthlyRent",
										"annualAmount",
										"rentalIncomeSince",
									]
								: key === "otherIncome"
									? ["incomeType", "annualAmount", "approximateStartDate"]
									: [];

				for (const field of fieldsToValidate) {
					const value = updatedIncomeData[id]?.[field];
					if (!value) {
						fieldErrors[field] = "This field is required";
						isValid = false;
					} else if (
						field === "postalCode" &&
						!validateCanadaPostalCode(value)
					) {
						fieldErrors[field] = "Invalid postal code format";
						isValid = false;
					}
				}

				updatedIncomeData[id] = {
					...updatedIncomeData[id],
					errors: fieldErrors,
				};
			}

			set({ incomeData: updatedIncomeData });
			return isValid;
		},
	}),
);

export { useIncomeStore };
